import { gsap } from 'gsap';
import { Container, Sprite, Text, type Texture } from 'pixi.js';

export type FloatingTextDropShadow = {
  color: number;
  alpha: number;
  angle: number;
  distance: number;
  blur: number;
};

// Optional icon stacked directly under the text. size = longest side in
// design-px; gap = px between the text baseline and the icon top.
export type FloatingTextIcon = { texture: Texture; size: number; gap: number };

export type FloatingTextOptions = {
  text: string;
  color: number;
  x: number;
  y: number;
  align?: 'left' | 'center' | 'right';
  fontSize?: number;
  riseBy?: number;
  durationSec?: number;
  resolution?: number;
  fontFamily?: string;
  letterSpacing?: number;
  dropShadow?: FloatingTextDropShadow;
  // When set, text + icon rise and fade together as one block.
  icon?: FloatingTextIcon;
};

// Spawns a text node at (x, y), floats it up while fading out, then destroys it.
// Fire-and-forget: caller does not need to await.
export function spawnFloatingText(parent: Container, opts: FloatingTextOptions): void {
  const text = new Text({
    text: opts.text,
    style: {
      fontFamily: opts.fontFamily
        ? `${opts.fontFamily}, system-ui, sans-serif`
        : 'system-ui, sans-serif',
      fontSize: opts.fontSize ?? 22,
      fill: opts.color,
      fontWeight: 'bold',
      stroke: { color: 0x000000, width: 3 },
      letterSpacing: opts.letterSpacing ?? 0,
      ...(opts.dropShadow ? { dropShadow: opts.dropShadow } : {}),
    },
    resolution: opts.resolution ?? 1,
  });
  const anchorX = opts.align === 'left' ? 0 : opts.align === 'right' ? 1 : 0.5;
  const rise = opts.riseBy ?? 50;
  const duration = opts.durationSec ?? 0.8;

  // With an icon: text + icon live in one container so they rise/fade as a
  // single block. The text keeps its bottom anchor at the block origin; the
  // icon hangs just below, horizontally centered under the word. The label is
  // edge-pinned (anchorX 0/1), so the word's center is offset by its measured
  // width: anchorX 0 -> +w/2, anchorX 1 -> -w/2, anchorX 0.5 -> 0.
  if (opts.icon) {
    const block = new Container();
    block.x = opts.x;
    block.y = opts.y;
    text.anchor.set(anchorX, 1);
    text.x = 0;
    text.y = 0;
    const icon = new Sprite(opts.icon.texture);
    const longest = Math.max(icon.texture.width, icon.texture.height) || 1;
    icon.scale.set(opts.icon.size / longest);
    icon.anchor.set(0.5, 0);
    icon.x = text.width * (0.5 - anchorX);
    icon.y = opts.icon.gap;
    block.addChild(text, icon);
    parent.addChild(block);
    gsap.to(block, {
      y: opts.y - rise,
      alpha: 0,
      duration,
      ease: 'power1.out',
      onComplete: () => {
        block.parent?.removeChild(block);
        // children:true destroys text + sprite; the shared icon texture is
        // NOT destroyed (default destroy opts), so it stays reusable.
        block.destroy({ children: true });
      },
    });
    return;
  }

  text.anchor.set(anchorX, 1);
  text.x = opts.x;
  text.y = opts.y;
  parent.addChild(text);

  gsap.to(text, {
    y: opts.y - rise,
    alpha: 0,
    duration,
    ease: 'power1.out',
    onComplete: () => {
      text.parent?.removeChild(text);
      text.destroy();
    },
  });
}
